import React, { Component, ComponentType, createElement, ReactNode, SyntheticEvent } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  flex,
  space,
  SpaceProps,
  width,
  WidthProps,
  MaxWidthProps,
  JustifyContentProps,
  margin,
  flexbox,
} from 'styled-system';
import { Box, emRhythm, hiddenOffScreen, lessThan } from 'localmed-core';

function idAsValue(item?: { id: string }): string | null {
  return item ? item.id : null;
}

function displayAsLabel(item?: { display: string }): ReactNode {
  return item ? item.display : '';
}
export type RadioGroupSize = 'sm' | 'md';

export type StyledSystemsProps = WidthProps & SpaceProps & MaxWidthProps & JustifyContentProps;

export interface Props extends StyledSystemsProps {
  /** HTML id for the containing div. Also used as a prefix for each label/input id. */
  id?: string;
  /** A name used to identity the group of selectables */
  name: string;
  /** The items which will be rendered as selectables */
  items: Array<any>;
  /** The items which are currently selected */
  value: any;
  /** Customize the label used for the item. Defaults to the `display` property of the item.. */
  toLabel: (item: any) => ReactNode;
  /** Customize the value used for item comparison. Defaults to the `id` property of the item. */
  toValue: (item: any) => string | number | undefined;
  /** Supplies the parent component with updated state */
  onChange?: (value: any, name: string) => any;
  /** Component used to display radioGroup items */
  itemDisplayComponent?: ComponentType<any>;
  itemWrapperProps?: object;
}

const RadioGroupBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'onChange',
})(
  css`
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
  `,
  lessThan(
    'lg',
    css`
      justify-content: center;
      margin: 0 auto;
    `
  ),
  space,
  width,
  margin,
  flexbox
);

const StyledRadioGroupWrapper = styled('div')(
  css`
    margin-bottom: ${emRhythm(0.5)};
  `,
  flex
);

const Label = styled.label`
  cursor: pointer;
`;

const Input = styled.input`
  margin-right: ${emRhythm(0.25)};
`;

class RadioGroup extends Component<Props> {
  static defaultProps = {
    value: null,
    toLabel: displayAsLabel,
    toValue: idAsValue,
    onChange: undefined,
    itemDisplayComponent: null,
  };

  state = {
    focused: false,
  };

  onInputChange = (item: any, event: SyntheticEvent<HTMLInputElement>) => {
    const { checked, name }: any = event.target;
    const { onChange, value } = this.props;

    let newValue = value;

    if (checked) {
      newValue = item;
    } else {
      newValue = null;
    }

    if (onChange !== undefined) onChange(newValue, name);
  };

  render(): ReactNode {
    const {
      name,
      items,
      itemDisplayComponent,
      value,
      toValue,
      toLabel,
      itemWrapperProps,
      ...props
    } = this.props;

    const itemIdPrefix = `${props.id || name}-item`;

    return (
      <RadioGroupBox {...props}>
        {items.map((item, index) => {
          const id = `${itemIdPrefix}-${index}`;
          const selected = toValue(value) === toValue(item);
          return (
            <StyledRadioGroupWrapper key={`${toValue(item)}-wrapper`} {...itemWrapperProps}>
              <Label id={id}>
                <Input
                  css={itemDisplayComponent && hiddenOffScreen}
                  onChange={this.onInputChange && ((event) => this.onInputChange(item, event))}
                  onFocus={() => this.setState({ focused: true })}
                  onBlur={() => this.setState({ focused: false })}
                  type="radio"
                  checked={selected}
                  id={`${id}-input`}
                  name={name}
                  value={toValue(item)}
                  data-testid="radioGroupInput"
                />
                {itemDisplayComponent
                  ? createElement(itemDisplayComponent, {
                      ...item,
                      selected,
                      focused: this.state.focused,
                    })
                  : toLabel(item)}
              </Label>
            </StyledRadioGroupWrapper>
          );
        })}
      </RadioGroupBox>
    );
  }
}

export default RadioGroup;
